import React from 'react'
import { Briefcase, Home, Heart, GraduationCap, Car, Building } from 'lucide-react'

interface DecisionTemplate {
  id: string
  name: string
  shortDescription: string
  icon: React.ReactNode
  title: string
  fullDescription: string
  options: string[]
  criteria: Array<{
    name: string
    weight: number
    description: string
  }>
}

const templates: DecisionTemplate[] = [
  {
    id: 'career-change',
    name: 'Career Change',
    shortDescription: 'Deciding whether to change jobs or careers',
    icon: <Briefcase className="w-6 h-6" />,
    title: 'Should I change my career?',
    fullDescription: 'Evaluating whether to pursue a new career path or stay in my current field.',
    options: [
      'Stay in current job and improve skills',
      'Switch to a different company in same field',
      'Change to a completely different industry',
      'Start my own business or freelance',
      'Go back to school for advanced degree',
      'Take a career break for personal development'
    ],
    criteria: [
      {
        name: 'Salary & Benefits',
        weight: 8,
        description: 'Financial compensation and benefits package'
      },
      {
        name: 'Work-Life Balance',
        weight: 7,
        description: 'Time flexibility and personal life integration'
      },
      {
        name: 'Growth Opportunities',
        weight: 8,
        description: 'Career advancement and skill development'
      },
      {
        name: 'Job Security',
        weight: 6,
        description: 'Stability and long-term employment prospects'
      },
      {
        name: 'Personal Interest',
        weight: 9,
        description: 'Passion and enjoyment in the work'
      }
    ]
  },
  {
    id: 'home-purchase',
    name: 'Home Purchase',
    shortDescription: 'Deciding whether to buy a house',
    icon: <Home className="w-6 h-6" />,
    title: 'Should I buy a house?',
    fullDescription: 'Evaluating the decision to purchase a home versus continuing to rent.',
    options: [
      'Buy a house in current location',
      'Continue renting in current place',
      'Buy a smaller/cheaper house',
      'Wait and save more money first',
      'Rent in a different area',
      'Buy a condo or townhouse instead'
    ],
    criteria: [
      {
        name: 'Financial Affordability',
        weight: 9,
        description: 'Ability to afford down payment and monthly payments'
      },
      {
        name: 'Location & Commute',
        weight: 8,
        description: 'Proximity to work, schools, and amenities'
      },
      {
        name: 'Future Plans',
        weight: 7,
        description: 'Long-term living situation and family plans'
      },
      {
        name: 'Market Conditions',
        weight: 6,
        description: 'Current real estate market trends'
      },
      {
        name: 'Maintenance Responsibility',
        weight: 5,
        description: 'Willingness to handle home maintenance'
      }
    ]
  },
  {
    id: 'relationship',
    name: 'Relationship Decision',
    shortDescription: 'Making important relationship choices',
    icon: <Heart className="w-6 h-6" />,
    title: 'What should I do about my relationship?',
    fullDescription: 'Evaluating the future of a romantic relationship.',
    options: [
      'Continue the relationship as is',
      'Take the relationship to the next level (marriage/commitment)',
      'End the relationship amicably',
      'Take a temporary break to think',
      'Seek couples counseling together',
      'Have an open conversation about concerns'
    ],
    criteria: [
      {
        name: 'Emotional Connection',
        weight: 9,
        description: 'Depth of emotional bond and understanding'
      },
      {
        name: 'Communication',
        weight: 8,
        description: 'Quality of communication and conflict resolution'
      },
      {
        name: 'Shared Values',
        weight: 8,
        description: 'Alignment of core values and life goals'
      },
      {
        name: 'Future Compatibility',
        weight: 7,
        description: 'Long-term compatibility and shared vision'
      },
      {
        name: 'Personal Growth',
        weight: 6,
        description: 'Support for individual growth and development'
      }
    ]
  },
  {
    id: 'education',
    name: 'Education Choice',
    shortDescription: 'Deciding on educational path',
    icon: <GraduationCap className="w-6 h-6" />,
    title: 'Which educational path should I choose?',
    fullDescription: 'Deciding between different educational options and programs.',
    options: [
      'Traditional 4-year university degree',
      'Online education program or MOOC',
      'Vocational training or trade school',
      'Self-study with professional certifications',
      'Apprenticeship or internship program',
      'Part-time study while working'
    ],
    criteria: [
      {
        name: 'Career Relevance',
        weight: 9,
        description: 'Relevance to desired career path'
      },
      {
        name: 'Cost & ROI',
        weight: 8,
        description: 'Cost of education and return on investment'
      },
      {
        name: 'Time Commitment',
        weight: 7,
        description: 'Time required to complete the program'
      },
      {
        name: 'Quality & Reputation',
        weight: 7,
        description: 'Quality of education and institutional reputation'
      },
      {
        name: 'Flexibility',
        weight: 6,
        description: 'Flexibility in scheduling and pace'
      }
    ]
  },
  {
    id: 'vehicle-purchase',
    name: 'Vehicle Purchase',
    shortDescription: 'Deciding on vehicle purchase',
    icon: <Car className="w-6 h-6" />,
    title: 'Which vehicle should I buy?',
    fullDescription: 'Choosing between different vehicle options and types.',
    options: [
      'Buy a new car with warranty',
      'Buy a used car for better value',
      'Buy an electric vehicle (EV)',
      'Buy a hybrid vehicle',
      'Lease a vehicle for flexibility',
      'Keep current vehicle and maintain it'
    ],
    criteria: [
      {
        name: 'Budget',
        weight: 9,
        description: 'Total cost including purchase, insurance, and maintenance'
      },
      {
        name: 'Fuel Efficiency',
        weight: 7,
        description: 'Fuel economy and environmental impact'
      },
      {
        name: 'Reliability',
        weight: 8,
        description: 'Vehicle reliability and maintenance costs'
      },
      {
        name: 'Features & Comfort',
        weight: 6,
        description: 'Desired features and comfort level'
      },
      {
        name: 'Resale Value',
        weight: 5,
        description: 'Future resale value and depreciation'
      }
    ]
  },
  {
    id: 'investment',
    name: 'Investment Decision',
    shortDescription: 'Making investment choices',
    icon: <Building className="w-6 h-6" />,
    title: 'How should I invest my money?',
    fullDescription: 'Deciding on investment strategies and portfolio allocation.',
    options: [
      'Stock market investments (ETFs/index funds)',
      'Real estate investment (rental property)',
      'Bonds and fixed income securities',
      'Cryptocurrency and digital assets',
      'Retirement accounts (401k/IRA)',
      'Keep money in high-yield savings'
    ],
    criteria: [
      {
        name: 'Risk Tolerance',
        weight: 9,
        description: 'Willingness to accept investment risk'
      },
      {
        name: 'Return Potential',
        weight: 8,
        description: 'Expected return on investment'
      },
      {
        name: 'Liquidity',
        weight: 6,
        description: 'Ability to quickly convert to cash'
      },
      {
        name: 'Time Horizon',
        weight: 7,
        description: 'Investment time frame and goals'
      },
      {
        name: 'Diversification',
        weight: 7,
        description: 'Portfolio diversification benefits'
      }
    ]
  }
]

interface DecisionTemplatesProps {
  onSelectTemplate: (template: DecisionTemplate) => void
}

export const DecisionTemplates: React.FC<DecisionTemplatesProps> = ({ onSelectTemplate }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Choose a Decision Template</h3>
        <p className="text-gray-600">Get started quickly with common decision scenarios</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="text-primary-600">
                {template.icon}
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{template.name}</h4>
                <p className="text-sm text-gray-600">{template.shortDescription}</p>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {template.options.length} options • {template.criteria.length} criteria
            </div>
          </button>
        ))}
      </div>
      
      <div className="text-center">
        <p className="text-sm text-gray-500">
          Or start with a blank decision analysis
        </p>
      </div>
    </div>
  )
} 